import { Link } from 'react-router-dom';
import { RACKSAVANT } from '@racksavant/core';
import { PASSWORD_RESET_NOTICE } from '@racksavant/ui';
import { LegalDoc, type LegalSection } from '../components/LegalDoc';

const LAST_UPDATED = '2 August 2026';

const SECTIONS: LegalSection[] = [
  {
    id: 'short',
    title: 'The short version',
    summary: 'Be 13+. Only upload photos you have the right to upload. Your looks are yours. Do not be a menace to anyone but fashion.',
    body: (
      <>
        <p>
          These are the rules for using RackSavant. The whole document matters, but if you only read
          one section, read this one.
        </p>
        <ul>
          <li>You need to be at least 13, or 16 in the EEA and UK.</li>
          <li>
            Only upload photographs of yourself, or of someone who has clearly agreed to it. Never
            of a stranger, an ex, or a child.
          </li>
          <li>
            What you make is yours. We take a limited licence to store it and show it to the people
            you have chosen to show it to.
          </li>
          <li>
            Paid plans renew until you cancel. Cancel any time; we refund the current period within
            14 days of an upgrade if it was not what you expected.
          </li>
          <li>
            Generated images are edits, not photographs. Do not present them as documentary evidence
            of anything.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'agreement',
    title: 'The agreement',
    summary: 'Using RackSavant means agreeing to these terms and the privacy policy.',
    body: (
      <>
        <p>
          These terms form a contract between you and RackSavant Labs, Inc. (“RackSavant”, “we”,
          “us”). They apply whenever you create an account or use the service on the web, on mobile
          or on desktop.
        </p>
        <p>
          Our <Link to="/privacy">privacy policy</Link> is part of this agreement. If you do not
          agree with either document, please do not use the service.
        </p>
      </>
    ),
  },
  {
    id: 'eligibility',
    title: 'Who can use RackSavant',
    summary: '13+ worldwide, 16+ in the EEA and UK. Under 18, keep your looks private.',
    body: (
      <>
        <p>
          You must be at least 13 years old to use RackSavant. If you are in the European Economic
          Area or the United Kingdom, you must be at least 16.
        </p>
        <p>
          If you are between the minimum age and 18, we ask that you keep your looks private rather
          than publishing them to the public Runway feed, and that a parent or guardian is aware you
          are using the service.
        </p>
        <p>
          You must not use RackSavant if you have previously been removed from it, or if the law
          where you live prohibits you from doing so.
        </p>
      </>
    ),
  },
  {
    id: 'account',
    title: 'Your account',
    summary: 'One account, real email, keep your password to yourself, tell us if it is compromised.',
    body: (
      <>
        <p>
          Give accurate information when you sign up and keep it current. You are responsible for
          activity under your account and for keeping your credentials secure. Tell us immediately
          at <a href="mailto:security@racksavant.com">security@racksavant.com</a> if you think
          someone else has access.
        </p>
        <p>
          Do not share an account between people, sell it, or transfer it. You can delete your
          account at any time from your settings; deletion is described in the{' '}
          <Link to="/privacy#retention">privacy policy</Link>.
        </p>
        <p>
          <strong>Recovering an account.</strong> {PASSWORD_RESET_NOTICE.title} — so recovery is
          manual: {PASSWORD_RESET_NOTICE.body}. We would rather say that plainly than leave you
          waiting on mail that is never going to arrive.
        </p>
      </>
    ),
  },
  {
    id: 'service',
    title: 'What the service is',
    summary: 'A try-on studio: you supply a photo, we render clothing onto it and write about the clothing.',
    body: (
      <>
        <p>
          RackSavant renders garments onto a photograph you provide, produces a written read of the
          resulting look, and gives you places to keep, discuss and publish those looks: lookbooks,
          fitting rooms, the Runway feed and drops.
        </p>
        <p>
          Free accounts get {RACKSAVANT.freeFittingsPerDay} fittings a day. Paid plans change limits
          and unlock features as described on the <Link to="/pricing">pricing page</Link>, which is
          incorporated into these terms.
        </p>
        <p>
          We may change, add or remove features. Where a change removes something material from a
          plan you are paying for, we will tell you in advance and you may cancel for a prorated
          refund.
        </p>
      </>
    ),
  },
  {
    id: 'billing',
    title: 'Subscriptions, billing and refunds',
    summary: 'Auto-renews until cancelled. 14-day refund on a new upgrade. Yearly is 12 months for the price of 10.',
    body: (
      <>
        <h3>Renewal</h3>
        <p>
          Paid plans renew automatically at the end of each billing period — monthly or yearly, as
          you chose — at the price in effect when you subscribed, until you cancel. Prices are in US
          dollars and exclude sales tax or VAT, which is added where applicable.
        </p>
        <h3>Cancelling</h3>
        <p>
          Cancel any time from your settings. Cancellation stops the next renewal; you keep the paid
          plan until the end of the period you have already paid for, then move to Free. We do not
          delete your content when a plan ends.
        </p>
        <h3>Refunds</h3>
        <p>
          If you upgrade and it is not what you expected, email{' '}
          <a href="mailto:billing@racksavant.com">billing@racksavant.com</a> within 14 days and we
          will refund that period in full. After 14 days, cancelling prevents the next charge rather
          than refunding the last one.
        </p>
        <p>
          Consumers in the UK and EU have statutory cancellation rights which apply in addition to
          this and are not limited by it. Where you ask us to begin providing the service
          immediately, you acknowledge that the statutory cooling-off period may be reduced
          proportionally to the service already used.
        </p>
        <h3>Price changes</h3>
        <p>
          We will give at least 30 days’ notice before a price change affects you, and you may
          cancel before it takes effect.
        </p>
      </>
    ),
  },
  {
    id: 'yourcontent',
    title: 'Your content, and the licence you give us',
    summary: 'You keep ownership. We get only the licence needed to run the product for you.',
    body: (
      <>
        <p>
          You own the photographs you upload and the looks, lookbooks, captions and drops you
          create. We claim no ownership over any of it.
        </p>
        <p>
          To operate the service, you grant us a non-exclusive, worldwide, royalty-free licence to
          host, store, reproduce, transmit and display your content — strictly for the purposes of
          providing RackSavant to you and to the people you have chosen to share with. This licence
          exists so that we can put your image in a fitting room and store the result. It ends when
          you delete the content or your account.
        </p>
        <p>
          We will not use your content in our own marketing without asking you first and getting a
          yes in writing. We do not use your photographs to train machine-learning models. See the{' '}
          <Link to="/privacy#ai">privacy policy</Link>.
        </p>
      </>
    ),
  },
  {
    id: 'licence',
    title: 'What you may do with what you make',
    summary: 'Personal use on every plan. Commercial use is included with Studio.',
    body: (
      <>
        <p>
          On <strong>Free</strong> and <strong>Muse</strong>, looks you generate are licensed for
          personal, non-commercial use: post them, send them, print them, put them in your own feed.
        </p>
        <p>
          On <strong>Studio</strong>, you additionally receive a commercial licence covering paid
          work — sponsored posts, client deliverables, advertising you are engaged to produce, and
          selling access to lookbooks you have made. That licence survives cancellation for content
          created while the plan was active.
        </p>
        <p>
          The licence covers our side only. Where a look features a third party’s garment, brand or
          trade mark, you remain responsible for any permission that party requires.
        </p>
      </>
    ),
  },
  {
    id: 'acceptable',
    title: 'Acceptable use',
    summary: 'No abuse, no illegal content, no scraping, no pretending a generated image is a photo.',
    body: (
      <>
        <p>Do not use RackSavant to:</p>
        <ul>
          <li>
            upload a photograph of another identifiable person without their clear, informed consent
            — see the next section;
          </li>
          <li>upload sexual content involving minors, or any sexual content involving anyone who has not consented;</li>
          <li>harass, threaten, demean or sexualise another person, in a room, in chat, or in a caption;</li>
          <li>impersonate somebody, or generate looks intended to make it appear that a real person wore or endorsed something they did not;</li>
          <li>present a generated image as an unaltered photograph in a context where that would deceive — news, evidence, dating profiles, identity documents;</li>
          <li>upload garment images you have no right to use;</li>
          <li>reverse engineer, scrape, resell or systematically extract the service or its outputs;</li>
          <li>circumvent plan limits, rate limits or watermarking;</li>
          <li>break the law, or use RackSavant to help someone else break it.</li>
        </ul>
        <p>
          We remove content that breaks these rules and suspend accounts that do it repeatedly or
          seriously. Report anything you see to{' '}
          <a href="mailto:safety@racksavant.com">safety@racksavant.com</a>.
        </p>
      </>
    ),
  },
  {
    id: 'others',
    title: 'Photographs of other people',
    summary: 'Only upload someone else if they have actually agreed. Never a child. Never an ex.',
    body: (
      <>
        <p>
          RackSavant is designed for photographs of yourself. If you upload a photograph of anybody
          else, you are confirming that you have their explicit, informed, current permission to use
          their image in this way, and that they are an adult.
        </p>
        <p>
          Uploading a photograph of a person who has not agreed — a stranger, a public figure, a
          former partner, a classmate, a child — is a serious breach of these terms and will result
          in the account being terminated, not warned.
        </p>
        <p>
          If someone has uploaded a photograph of you, write to{' '}
          <a href="mailto:safety@racksavant.com">safety@racksavant.com</a>. We will remove the
          content and any looks made from it, and we do not need a lawyer’s letter first.
        </p>
      </>
    ),
  },
  {
    id: 'garments',
    title: 'Uploading garments',
    summary: 'Studio only. You must own the images or be authorised to use them.',
    body: (
      <>
        <p>
          Studio subscribers may upload their own garment images. By doing so you confirm that you
          own the images or have permission from the rights holder to upload, store and display
          them, including in any drop you publish.
        </p>
        <p>
          Do not upload images scraped from a retailer, a brand’s campaign photography you have not
          been given, or a competitor’s catalogue. We will remove uploads on a credible complaint,
          and repeat offences end the account.
        </p>
      </>
    ),
  },
  {
    id: 'drops',
    title: 'Drops and publishing',
    summary: 'Publishing a drop makes it public and lets others fit it. You stay responsible for it.',
    body: (
      <>
        <p>
          Publishing a drop makes the garment set and its title, description and cover publicly
          accessible to anyone with the link, and allows other users to generate their own looks
          from those garments on their own photographs.
        </p>
        <p>
          You remain responsible for what is in a drop, including any brand permissions and any
          disclosure your jurisdiction requires for paid promotion. Closing a drop stops new
          fittings; looks that other users already generated remain in their own accounts, because
          they belong to them.
        </p>
      </>
    ),
  },
  {
    id: 'rooms',
    title: 'Fitting rooms',
    summary: 'Ephemeral, invite-only, and everyone in there can see what you bring in.',
    body: (
      <>
        <p>
          A fitting room is a temporary shared space. Everyone holding the code can see the looks
          you bring into it, react to them, and read the chat. Rooms and their messages are deleted
          twelve hours after creation.
        </p>
        <p>
          Only invite people you want looking. Do not screenshot and redistribute someone else’s
          looks from a room without asking them — it is not what the room is for, and we will act on
          reports that it happened.
        </p>
      </>
    ),
  },
  {
    id: 'ai',
    title: 'What generated images are, and are not',
    summary: 'They are AI edits. Not photographs, not fit advice, not a size guarantee.',
    body: (
      <>
        <p>
          Looks are produced by an image model. They are illustrative edits showing how a garment
          might read on you. They are not photographs, and they are not a promise about fit, size,
          fabric behaviour, colour accuracy or availability.
        </p>
        <p>
          The model is instructed to leave your face, body, pose, skin and age unchanged and to
          alter only clothing. Models are imperfect and occasionally get this wrong. If you see
          output that alters your body or writes about it, send it to{' '}
          <a href="mailto:safety@racksavant.com">safety@racksavant.com</a> — that is a bug of the
          most serious kind here and we want to see it.
        </p>
        <p>
          Do not rely on a look to make a purchase decision that you would not otherwise make, and
          do not present one as documentary evidence of anything.
        </p>
      </>
    ),
  },
  {
    id: 'dmca',
    title: 'Copyright complaints',
    summary: 'Send a DMCA notice to copyright@racksavant.com. Counter-notices welcome. Repeat infringers are removed.',
    body: (
      <>
        <p>
          If you believe material on RackSavant infringes your copyright, send a notice to{' '}
          <a href="mailto:copyright@racksavant.com">copyright@racksavant.com</a> including: your
          contact details; identification of the copyrighted work; the URL of the material you want
          removed; a statement that you have a good-faith belief the use is not authorised; a
          statement under penalty of perjury that the information is accurate and you are authorised
          to act; and your signature.
        </p>
        <p>
          We remove material that is the subject of a valid notice and tell the user who posted it.
          If you believe your material was removed in error, you may send a counter-notice to the
          same address and we will follow the process the DMCA sets out.
        </p>
        <p>We terminate the accounts of repeat infringers.</p>
      </>
    ),
  },
  {
    id: 'availability',
    title: 'Availability and changes',
    summary: 'We aim for it to be up. We do not promise it always will be.',
    body: (
      <>
        <p>
          We work to keep RackSavant available and fast, but we do not guarantee uninterrupted
          service. Maintenance, third-party outages and model provider incidents happen, and
          generation queues can slow at peak.
        </p>
        <p>
          Features marked as beta or experimental may change or disappear without notice. Where a
          paid plan is unusable for an extended period because of us, write to us and we will credit
          the time.
        </p>
      </>
    ),
  },
  {
    id: 'termination',
    title: 'Suspension and termination',
    summary: 'You can leave any time. We remove accounts that break the safety rules.',
    body: (
      <>
        <p>
          You may stop using RackSavant and delete your account at any time. We may suspend or
          terminate an account that breaches these terms, creates legal risk for us or other users,
          or is being used for the things listed under acceptable use.
        </p>
        <p>
          Where it is reasonable and safe to do so, we will warn you first and give you a chance to
          export your content. For breaches involving photographs of non-consenting people or
          sexual content involving minors, we terminate immediately and report where the law
          requires it.
        </p>
      </>
    ),
  },
  {
    id: 'disclaimers',
    title: 'Disclaimers',
    summary: 'Provided as-is beyond what the law requires us to guarantee.',
    body: (
      <>
        <p>
          Except for warranties that cannot be excluded by law, RackSavant is provided “as is” and
          “as available”, without warranties of any kind, express or implied, including
          merchantability, fitness for a particular purpose and non-infringement.
        </p>
        <p>
          We do not warrant that generated output will be accurate, that garments will fit as
          depicted, or that the service will meet any particular requirement. Nothing in these terms
          excludes liability for death or personal injury caused by negligence, fraud, or anything
          else that cannot lawfully be excluded — and if you are a consumer, your statutory rights
          are unaffected.
        </p>
      </>
    ),
  },
  {
    id: 'liability',
    title: 'Limitation of liability',
    summary: 'Capped at what you paid us in the last 12 months, or $100 if that is more.',
    body: (
      <>
        <p>
          To the maximum extent permitted by law, neither party is liable for indirect, incidental,
          special, consequential or punitive damages, or for lost profits, revenue, data or
          goodwill, arising out of or related to this agreement.
        </p>
        <p>
          Our total aggregate liability arising out of or relating to the service is limited to the
          greater of the amount you paid us in the twelve months before the claim arose, or one
          hundred US dollars.
        </p>
        <p>
          These limits do not apply to liability that cannot be limited by law, including consumer
          rights in the UK and EU.
        </p>
      </>
    ),
  },
  {
    id: 'indemnity',
    title: 'Indemnity',
    summary: 'If your content gets us sued, you cover it. Consumers, see the carve-out.',
    body: (
      <>
        <p>
          You agree to indemnify and hold RackSavant harmless from claims, damages and reasonable
          legal costs arising from content you upload or publish, your breach of these terms, or
          your infringement of someone else’s rights.
        </p>
        <p>
          This does not apply to consumers acting outside a trade or profession, except where the
          claim arises from content you uploaded in breach of the sections on photographs of other
          people or acceptable use.
        </p>
      </>
    ),
  },
  {
    id: 'law',
    title: 'Governing law and disputes',
    summary: 'California law. Talk to us first. Consumers keep their local courts.',
    body: (
      <>
        <p>
          These terms are governed by the laws of the State of California, excluding its conflict of
          laws rules, and the state and federal courts located in San Francisco County have
          exclusive jurisdiction — except that consumers may bring proceedings in the courts of
          their own country of residence and benefit from its mandatory consumer protection law.
        </p>
        <p>
          Before filing anything, please email{' '}
          <a href="mailto:legal@racksavant.com">legal@racksavant.com</a>. We commit to trying to
          resolve disputes informally within 30 days, and in our experience that is usually enough.
        </p>
      </>
    ),
  },
  {
    id: 'changes',
    title: 'Changes to these terms',
    summary: 'Material changes get 30 days’ notice. Continuing to use it means accepting them.',
    body: (
      <>
        <p>
          We will update these terms as the product changes. The date at the top reflects the
          current version.
        </p>
        <p>
          For material changes we will give at least 30 days’ notice by email and in the app.
          Continuing to use RackSavant after a change takes effect means you accept it; if you do
          not, cancel and delete your account, and we will refund any unused paid period.
        </p>
      </>
    ),
  },
  {
    id: 'contact',
    title: 'Contact',
    summary: 'legal@ for contracts, safety@ for people, billing@ for money.',
    body: (
      <>
        <p>
          Legal and contractual: <a href="mailto:legal@racksavant.com">legal@racksavant.com</a>.
        </p>
        <p>
          Safety, harassment and non-consensual images:{' '}
          <a href="mailto:safety@racksavant.com">safety@racksavant.com</a>.
        </p>
        <p>
          Billing: <a href="mailto:billing@racksavant.com">billing@racksavant.com</a>. Copyright:{' '}
          <a href="mailto:copyright@racksavant.com">copyright@racksavant.com</a>. Everything else:{' '}
          <a href="mailto:hello@racksavant.com">hello@racksavant.com</a>.
        </p>
      </>
    ),
  },
];

export function TermsPage() {
  return (
    <LegalDoc
      eyebrow="Legal"
      title="Terms of service"
      subtitle="The rules for using RackSavant, written so you can actually check whether we are being reasonable."
      lastUpdated={LAST_UPDATED}
      contactEmail="legal@racksavant.com"
      sections={SECTIONS}
      siblingLabel="Read the privacy policy"
      siblingTo="/privacy"
    />
  );
}
